import './DiceTable.scss';
import React, { FunctionComponent, ReactElement } from 'react';
import dayjs from 'dayjs';
import Table from 'react-bootstrap/Table';
import Utils from '../../../shared/Utils';

export type DiceRowSpanIndex = { [tableRowIndex: number]: number };

export interface DiceTableRowInterface {
    columns: ReactElement[];
    columnsToNest?: ReactElement[][];
    columnsToHideInSmallMode?: number[];
    rowSpanByColumnIndex?: DiceRowSpanIndex;
}

export interface DiceTableProps {
    tableName: string;
    headerNames: string[];
    headersToHideInSmallMode?: number[];
    rows: DiceTableRowInterface[];
    noRowsMessage?: string;
}

export const unavailableText: ReactElement = <span className="font-italic">Unavailable</span>;
export const notApplicableText: ReactElement = <span>N/A</span>;

export const valueOrUnavailable = (value: string) => <span>{value || unavailableText}</span>;

export const formattedDateCell = (
    date: dayjs.Dayjs | undefined,
    useIfDataFalsy: ReactElement,
): ReactElement => {
    const formattedDate = Utils.formatDate(date);
    if (formattedDate) {
        return <div>{formattedDate}</div>;
    }
    return useIfDataFalsy;
};

const generateClassName = (hideInSmallMode: boolean): string | undefined =>
    hideInSmallMode ? 'hide-in-small-mode' : undefined;

const DiceTableHeaderCell = (
    name: string,
    index: number,
    hideInSmallMode: boolean,
): ReactElement => (
    <th key={`header-${index}`} className={generateClassName(hideInSmallMode)}>
        {name}
    </th>
);

const DiceTableHeaderRow = (
    headerNames: string[] = [],
    headersToHideInSmallMode: number[] = [],
): ReactElement => (
    <tr>
        {headerNames.map((headerName, index) =>
            DiceTableHeaderCell(headerName, index, !!headersToHideInSmallMode?.includes(index)),
        )}
    </tr>
);

const DiceTableCell = (
    element: ReactElement,
    key: string,
    hideInSmallMode: boolean,
    rowSpan?: number | undefined,
): ReactElement => (
    <td key={key} className={generateClassName(hideInSmallMode)} rowSpan={rowSpan}>
        {element}
    </td>
);

const DiceTableRow = (
    {
        columns,
        columnsToNest,
        columnsToHideInSmallMode,
        rowSpanByColumnIndex,
    }: DiceTableRowInterface,
    index: number,
): ReactElement => {
    const rowSpan = rowSpanByColumnIndex && rowSpanByColumnIndex[0] ? rowSpanByColumnIndex[0] : 1;

    const mainRow = (
        <tr key={`row-${index}`}>
            {columns.map((column, idx) =>
                DiceTableCell(
                    column,
                    `row-${index}-col-${idx}`,
                    !!columnsToHideInSmallMode?.includes(idx),
                    rowSpanByColumnIndex && rowSpanByColumnIndex[idx]
                        ? rowSpanByColumnIndex[idx]
                        : undefined,
                ),
            )}
        </tr>
    );

    const nestedRows = [];
    for (let i = 1; i < rowSpan; i++) {
        nestedRows.push(
            <tr key={`nested-row-${index}-${i}`}>
                {columns.map((_, colIdx) => {
                    const nestedColumn =
                        columnsToNest && columnsToNest[colIdx]
                            ? columnsToNest[colIdx][i - 1]
                            : undefined;
                    return nestedColumn
                        ? DiceTableCell(
                              nestedColumn,
                              `nested-row-${index}-${i}-col-${colIdx}`,
                              !!columnsToHideInSmallMode?.includes(colIdx),
                          )
                        : null;
                })}
            </tr>,
        );
    }

    return (
        <>
            {mainRow}
            {nestedRows}
        </>
    );
};

const FullColSpanMessage = (message: string | undefined): ReactElement => (
    <tr>
        <td colSpan={999}>
            <div className="text-center">{message || 'No records found.'}</div>
        </td>
    </tr>
);

const DiceTable: FunctionComponent<DiceTableProps> = ({
    tableName,
    headerNames,
    headersToHideInSmallMode,
    rows,
    noRowsMessage,
}) => (
    <Table title={tableName} className="dice-table" bordered>
        <thead>{DiceTableHeaderRow(headerNames, headersToHideInSmallMode)}</thead>
        <tbody>
            {rows.length > 0
                ? rows.map((row, index) => DiceTableRow(row, index))
                : FullColSpanMessage(noRowsMessage)}
        </tbody>
    </Table>
);

export default DiceTable;
